import { useCallback, useEffect, useState } from "react";

import type { ContactSmartDefaults } from "../@types/sharedSchema";
import useWebotServer from "./useWebotServer";

/**
 * Per-realtor "smart defaults" for the new/edit-contact modal (suggested areas,
 * budget ranges, and property types — computed on the server from the realtor's
 * settings + their last ~50 contacts).
 *
 * Fetched once per session and kept in a module-level cache so the defaults are
 * already in memory by the time the modal opens (prefetch it from the contacts
 * page). The server owns freshness: its single GET serves the Firestore cache
 * and recomputes synchronously when it's missing or stale.
 */
let cachedDefaults: ContactSmartDefaults | null = null;
let inFlight: Promise<ContactSmartDefaults | null> | null = null;

const useContactSmartDefaults = (options?: { prefetch?: boolean }) => {
  const { getContactSmartDefaults } = useWebotServer();
  const [defaults, setDefaults] = useState<ContactSmartDefaults | null>(cachedDefaults);
  const [loading, setLoading] = useState<boolean>(!cachedDefaults);

  const load = useCallback(async (): Promise<ContactSmartDefaults | null> => {
    if (cachedDefaults) {
      setDefaults(cachedDefaults);
      setLoading(false);
      return cachedDefaults;
    }
    if (!inFlight) {
      inFlight = (async () => {
        try {
          const res = await getContactSmartDefaults();
          cachedDefaults = res?.defaults ?? null;
          return cachedDefaults;
        } catch {
          return null;
        } finally {
          inFlight = null;
        }
      })();
    }
    const result = await inFlight;
    setDefaults(result);
    setLoading(false);
    return result;
  }, [getContactSmartDefaults]);

  useEffect(() => {
    if (options?.prefetch === false) return;
    void load();
  }, [load, options?.prefetch]);

  return { defaults, loading, reload: load };
};

export default useContactSmartDefaults;
